import {
	Bar,
	CartesianGrid,
	Cell,
	ComposedChart,
	Line,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'
import { useIntlayer } from 'react-intlayer'
import { useOperationalReport } from '../hooks/use-operational-report'

type ChartDatum = {
	name: string
	current: number
	previous?: number
	line?: number
}

type DonutDatum = {
	name: string
	value: number
	color: string
}

type LegendItem = {
	label: string
	color: string
}

type TooltipPayload = {
	dataKey?: string
	value?: unknown
	payload?: Record<string, unknown>
}

type TooltipProps = {
	active?: boolean
	payload?: TooltipPayload[]
	label?: string
}

const chartFont = 'var(--font-hedvig-sans)'
const grayShades = ['hsl(var(--foreground))', '#707070', '#A0A0A0', '#606060'] as const

const compactNumber = (value: number) => {
	const absolute = Math.abs(value)
	const sign = value < 0 ? '-' : ''
	if (absolute >= 1_000_000) return `${sign}${(absolute / 1_000_000).toFixed(1)}M`
	if (absolute >= 1_000) return `${sign}${(absolute / 1_000).toFixed(0)}k`
	return value.toString()
}

const tooltipValue = (value: unknown) => (typeof value === 'number' ? value.toLocaleString() : String(value ?? '0'))

const ReportTooltip = ({ active, payload, label }: TooltipProps) => {
	if (!active || !payload?.length) return null
	return (
		<div className='border p-2 text-[10px] font-hedvig-sans bg-background border-e6e6e6 text-foreground shadow-sm'>
			<p className='mb-1 text-707070'>{label}</p>
			{payload.map((entry, index) => (
				<p key={`${entry.dataKey ?? 'value'}-${index}`} className='text-foreground capitalize'>
					{entry.dataKey}: {tooltipValue(entry.value)}
				</p>
			))}
		</div>
	)
}

const DonutTooltip = ({ active, payload }: TooltipProps) => {
	if (!active || !payload?.length) return null
	const current = payload[0]?.payload
	const name = typeof current?.name === 'string' ? current.name : ''
	const value = typeof current?.value === 'number' ? current.value : 0
	return (
		<div className='border p-2 text-[10px] bg-background border-e6e6e6 text-foreground shadow-sm'>
			<p className='mb-1 text-707070'>{name}</p>
			<p className='text-foreground'>{value.toLocaleString()}</p>
		</div>
	)
}

type MetricCardProps = {
	title: string
	value: string
	legend: LegendItem[]
	children: React.ReactNode
}

const MetricCard = ({ title, value, legend, children }: MetricCardProps) => (
	<div className='border bg-background border-border p-6 flex flex-col h-full relative group'>
		<div className='mb-4 min-h-[140px]'>
			<div className='flex items-start justify-between h-7'>
				<h3 className='text-sm font-normal text-muted-foreground'>{title}</h3>
			</div>
			<p className='text-3xl font-normal mb-3'>{value}</p>
			<div className='flex items-center gap-4 mt-2 flex-wrap'>
				{legend.map(item => (
					<div key={item.label} className='flex gap-2 items-center'>
						<div className='w-2 h-2' style={{ backgroundColor: item.color }} />
						<span className='text-xs text-muted-foreground'>{item.label}</span>
					</div>
				))}
			</div>
		</div>
		<div className='h-80'>{children}</div>
	</div>
)

const BarComparisonChart = ({ data }: { data: ChartDatum[] }) => (
	<ResponsiveContainer width='100%' height='100%' debounce={1}>
		<ComposedChart data={data} margin={{ top: 6, right: 6, left: -20, bottom: 6 }}>
			<CartesianGrid strokeDasharray='3 3' stroke='var(--chart-grid-stroke)' />
			<XAxis
				dataKey='name'
				axisLine={false}
				tickLine={false}
				tick={{ fill: 'var(--chart-axis-text)', fontSize: 10, fontFamily: chartFont }}
			/>
			<YAxis
				axisLine={false}
				tickLine={false}
				tick={{ fill: 'var(--chart-axis-text)', fontSize: 10, fontFamily: chartFont }}
				tickFormatter={compactNumber}
			/>
			<Tooltip content={<ReportTooltip />} wrapperStyle={{ zIndex: 9999 }} />
			<Bar dataKey='previous' fill='var(--chart-bar-fill-secondary)' isAnimationActive={false} />
			<Bar dataKey='current' fill='var(--chart-bar-fill)' isAnimationActive={false} />
			<Line
				type='monotone'
				dataKey='line'
				stroke='var(--chart-line-secondary)'
				strokeWidth={1}
				strokeDasharray='5 5'
				dot={false}
				isAnimationActive={false}
			/>
		</ComposedChart>
	</ResponsiveContainer>
)

const StackedOperationsChart = ({ data }: { data: ChartDatum[] }) => (
	<ResponsiveContainer width='100%' height='100%' debounce={1}>
		<ComposedChart data={data} barGap={15} margin={{ top: 6, right: 6, left: -20, bottom: 6 }}>
			<defs>
				<pattern id='report-raster' patternUnits='userSpaceOnUse' width='64' height='64'>
					<rect width='64' height='64' className='fill-C6C6C6' />
					<path d='M-106 110L22 -18' className='stroke-323232' />
					<path d='M-98 110L30 -18' className='stroke-323232' />
					<path d='M-90 110L38 -18' className='stroke-323232' />
					<path d='M-82 110L46 -18' className='stroke-323232' />
					<path d='M-74 110L54 -18' className='stroke-323232' />
				</pattern>
			</defs>
			<CartesianGrid strokeDasharray='3 3' stroke='var(--chart-grid-stroke)' />
			<XAxis
				dataKey='name'
				axisLine={false}
				tickLine={false}
				tick={{ fill: 'var(--chart-axis-text)', fontSize: 10, fontFamily: chartFont }}
			/>
			<YAxis
				axisLine={false}
				tickLine={false}
				tick={{ fill: 'var(--chart-axis-text)', fontSize: 10, fontFamily: chartFont }}
				tickFormatter={compactNumber}
			/>
			<Tooltip content={<ReportTooltip />} cursor={false} />
			<Bar barSize={16} dataKey='previous' stackId='a' fill='url(#report-raster)' />
			<Bar barSize={16} dataKey='current' stackId='a' className='fill-C6C6C6' />
			<Line type='monotone' dataKey='line' strokeWidth={2.5} stroke='hsl(var(--border))' dot={false} />
		</ComposedChart>
	</ResponsiveContainer>
)

const DonutChart = ({ data }: { data: DonutDatum[] }) => (
	<div className='relative h-full'>
		<div
			className='absolute inset-0 dark:hidden'
			style={{
				backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px)',
				backgroundSize: '12px 12px',
			}}
		/>
		<div
			className='absolute inset-0 hidden dark:block'
			style={{
				backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)',
				backgroundSize: '12px 12px',
			}}
		/>
		<ResponsiveContainer width='100%' height='100%' debounce={1} className='relative'>
			<PieChart>
				<Pie
					data={data}
					cx='50%'
					cy='50%'
					innerRadius={80}
					outerRadius={120}
					fill='hsl(var(--foreground))'
					dataKey='value'
					paddingAngle={1}
					stroke='none'
				>
					{data.map((entry, index) => (
						<Cell key={`${entry.name}-${index}`} fill={entry.color} stroke='none' />
					))}
				</Pie>
				<Tooltip content={<DonutTooltip />} />
			</PieChart>
		</ResponsiveContainer>
	</div>
)

const LoadingReport = ({ label }: { label: string }) => (
	<div className='pt-6 pb-16 space-y-6'>
		<div className='border bg-background border-border p-6 text-sm text-606060'>{label}</div>
	</div>
)

const ReportsPage = () => {
	const content = useIntlayer('reports-page')
	const { report, isLoading } = useOperationalReport()

	if (isLoading || !report) return <LoadingReport label={content.loading.value} />

	const pendingTimeOff = report.timeOff.pendingDepartment + report.timeOff.pendingHr
	const workflowTotal = report.workflows.active + report.workflows.draft + report.workflows.archived
	const customerDonut = [
		{ name: content.labels.enriched.value, value: report.customers.enriched, color: grayShades[0] },
		{ name: content.labels.needsEnrichment.value, value: report.customers.needsEnrichment, color: grayShades[1] },
	]
	const teamDonut = [
		{ name: content.labels.active.value, value: report.team.activeMembers, color: grayShades[0] },
		{
			name: content.cards.teamDetail.value,
			value: Math.max(0, report.team.totalMembers - report.team.activeMembers),
			color: grayShades[1],
		},
	]

	return (
		<>
			<div className='grid grid-cols-12 gap-6 pb-20' data-metrics-grid>
				<div className='col-span-12 lg:col-span-8'>
					<MetricCard
						title={content.cards.activeWork.value}
						value={String(report.workItems.active)}
						legend={[
							{ label: content.labels.active.value, color: 'var(--chart-bar-fill)' },
							{ label: content.labels.archived.value, color: 'var(--chart-bar-fill-secondary)' },
							{ label: content.labels.draft.value, color: 'var(--chart-line-secondary)' },
						]}
					>
						<BarComparisonChart
							data={[
								{
									name: content.labels.active.value,
									current: report.workItems.active,
									previous: report.workflows.active,
									line: workflowTotal,
								},
								{
									name: content.labels.archived.value,
									current: report.workItems.archived,
									previous: report.workflows.archived,
									line: workflowTotal,
								},
								{ name: content.labels.draft.value, current: report.workflows.draft, previous: 0, line: workflowTotal },
								{
									name: content.cards.vault.value,
									current: report.vault.active,
									previous: report.vault.archived,
									line: workflowTotal,
								},
							]}
						/>
					</MetricCard>
				</div>

				<div className='col-span-12 lg:col-span-4'>
					<MetricCard
						title={content.cards.customers.value}
						value={String(report.customers.total)}
						legend={customerDonut.map(item => ({ label: item.name, color: item.color }))}
					>
						<DonutChart data={customerDonut} />
					</MetricCard>
				</div>

				<div className='col-span-12 md:col-span-6'>
					<MetricCard
						title={content.cards.timeOff.value}
						value={String(pendingTimeOff)}
						legend={[
							{ label: content.labels.pendingDepartment.value, color: 'var(--chart-bar-fill)' },
							{ label: content.labels.pendingHr.value, color: 'var(--chart-bar-fill-secondary)' },
						]}
					>
						<StackedOperationsChart
							data={[
								{
									name: content.labels.pendingDepartment.value,
									current: report.timeOff.pendingDepartment,
									previous: report.timeOff.pendingHr,
									line: pendingTimeOff,
								},
								{
									name: content.labels.approved.value,
									current: report.timeOff.approved,
									previous: report.timeOff.rejected,
									line: pendingTimeOff,
								},
								{
									name: content.labels.rejected.value,
									current: report.timeOff.rejected,
									previous: 0,
									line: pendingTimeOff,
								},
							]}
						/>
					</MetricCard>
				</div>

				<div className='col-span-12 md:col-span-6'>
					<MetricCard
						title={content.cards.vault.value}
						value={String(report.vault.active)}
						legend={[
							{ label: content.labels.active.value, color: 'var(--chart-bar-fill)' },
							{ label: content.labels.archived.value, color: 'var(--chart-bar-fill-secondary)' },
						]}
					>
						<BarComparisonChart
							data={[
								{
									name: content.sections.documents.value,
									current: report.vault.active,
									previous: report.vault.archived,
									line: report.vault.active,
								},
								{
									name: content.sections.people.value,
									current: report.team.activeMembers,
									previous: Math.max(0, report.team.totalMembers - report.team.activeMembers),
									line: report.team.totalMembers,
								},
								{
									name: content.sections.customers.value,
									current: report.customers.enriched,
									previous: report.customers.needsEnrichment,
									line: report.customers.total,
								},
							]}
						/>
					</MetricCard>
				</div>

				<div className='col-span-12 md:col-span-6 lg:col-span-4'>
					<MetricCard
						title={content.cards.team.value}
						value={String(report.team.activeMembers)}
						legend={teamDonut.map(item => ({ label: item.name, color: item.color }))}
					>
						<DonutChart data={teamDonut} />
					</MetricCard>
				</div>

				<div className='col-span-12 md:col-span-6 lg:col-span-8'>
					<MetricCard
						title={content.cards.workflows.value}
						value={String(report.workflows.active)}
						legend={[
							{ label: content.labels.active.value, color: 'var(--chart-bar-fill)' },
							{ label: content.labels.draft.value, color: 'var(--chart-bar-fill-secondary)' },
							{ label: content.labels.archived.value, color: 'var(--chart-line-secondary)' },
						]}
					>
						<BarComparisonChart
							data={[
								{
									name: content.labels.active.value,
									current: report.workflows.active,
									previous: report.workItems.active,
									line: workflowTotal,
								},
								{
									name: content.labels.draft.value,
									current: report.workflows.draft,
									previous: pendingTimeOff,
									line: workflowTotal,
								},
								{
									name: content.labels.archived.value,
									current: report.workflows.archived,
									previous: report.workItems.archived,
									line: workflowTotal,
								},
							]}
						/>
					</MetricCard>
				</div>
			</div>
		</>
	)
}

export { ReportsPage }
